<template>
  <div class="ds-scope ca-header__mobile-menu" :class="{ open: open }">
    <div class="ca-header__mobile-menu-top mt-size-5 py-size-4 px-size-6">
      <a
        :href="logoLink"
        class="ca-header__logo-ca"
        aria-label="Ir para a página inicial"
      >
        <img :src="logoImageUrl" alt="Logo CA" />
      </a>
      <button
        class="ca-header__mobile-menu-close rounded-sm"
        @click="emit('close')"
        aria-label="Fechar menu"
      >
        <component :is="Close" />
      </button>
    </div>

    <!-- Tab buttons -->
    <div class="ca-header__button-wrapper flex gap-size-2 p-size-4">
      <!-- Mobile menu normal -->
      <template v-if="!isSimplifiedMenu">
        <button
          v-for="(item, colIdx) in homeItems"
          :key="`tab-${colIdx}`"
          class="ca-header__button rounded-xs gap-size-3 py-size-3 px-size-5"
          :class="{
            'is-active': activeIndex === colIdx,
          }"
          @click="emit('select-tab', colIdx, $event)"
        >
          <span class="ca-header__title caption-large text-theme-primary">{{
            item.title
          }}</span>
        </button>
      </template>

      <!-- Mobile menu simplificado -->
      <template v-if="isSimplifiedMenu">
        <button
          v-for="(item, colIdx) in simplifiedItems"
          :key="`tab-simplified-${colIdx}`"
          class="ca-header__button rounded-xs gap-size-3 py-size-3 px-size-5"
          :class="{
            'is-active':
              item.url &&
              (currentPath === item.url ||
                currentPath.startsWith(item.url + '/')),
          }"
          @click.prevent="emit('navigate-category', item)"
        >
          <span class="ca-header__title caption-large text-theme-primary">{{
            item.title
          }}</span>
        </button>
      </template>
    </div>

    <!-- Conteúdo accordion - apenas no menu normal -->
    <template v-if="!isSimplifiedMenu">
      <hr class="ca-header__megamenu-separator-mobile" />
      <div class="ca-header__mobile-menu-content">
        <div class="ca-header__mobile-megamenu-header py-size-4 px-size-5">
          <a
            :href="activeItem?.url"
            class="ca-header__mobile-megamenu-header-title caption-action inline-flex items-center gap-size-3 text-theme-primary"
          >
            {{ activeItem?.title }}
            <component :is="ArrowRight" />
          </a>
        </div>

        <div
          v-for="(category, sIdx) in activeItem?.categories || []"
          :key="`accordion-${sIdx}`"
          class="ca-header__mobile-menu-accordion"
        >
          <!-- Investor Relations: apenas título + chevron right, navega direto -->
          <a
            v-if="isInvestorRelations"
            :href="category.url"
            class="ca-header__mobile-menu-accordion-title caption-action flex items-center justify-between py-size-4 px-size-5"
          >
            {{ category.title }}
            <component
              :is="ChevronDown"
              class="ca-header__mobile-menu-accordion-icon ca-header__mobile-menu-accordion-icon--right"
            />
          </a>

          <!-- Accordion normal -->
          <template v-else>
            <button
              class="ca-header__mobile-menu-accordion-title caption-action flex items-center justify-between py-size-4 px-size-5"
              @click="emit('toggle-accordion', sIdx)"
            >
              {{ category.title }}
              <component
                :is="ChevronDown"
                class="ca-header__mobile-menu-accordion-icon"
                :class="{ expanded: isAccordionOpen(sIdx) }"
              />
            </button>
            <div
              class="ca-header__mobile-menu-accordion-panel"
              :class="{ expanded: isAccordionOpen(sIdx) }"
            >
              <ul class="ca-header__mobile-menu-accordion-list flex flex-col">
                <li
                  v-for="(item, lIdx) in category.items"
                  :key="`link-${sIdx}-${lIdx}`"
                >
                  <a
                    :href="item.url"
                    :target="item.target || '_self'"
                    class="ca-header__mobile-menu-accordion-link caption-large flex items-center gap-size-3 py-size-3 px-size-5"
                  >
                    {{ item.title }}
                    <CaStatusTag v-if="item.tag" :label="item.tag" />
                  </a>
                </li>
                <li :key="`see-all-${sIdx}`" v-if="category.url">
                  <a
                    :href="category.url"
                    class="ca-header__mobile-menu-accordion-link caption-large flex items-center gap-size-3 py-size-3 px-size-5"
                  >
                    Ver tudo {{ category.title }}
                  </a>
                </li>
              </ul>
            </div>
          </template>
        </div>
      </div>
    </template>

    <!-- Footer buttons - apenas no menu normal -->
    <div
      class="ca-header__mobile-menu-footer flex pt-size-6 px-size-5 pb-size-5"
      v-if="!isSimplifiedMenu"
    >
      <button
        class="ca-header__mobile-menu-footer-btn rounded-xs ca-header__mobile-menu-footer-btn--secondary caption-large flex items-center justify-center py-size-3 px-size-4"
        @click="emit('homebanking')"
      >
        <component :is="LockIcon" />{{ homebankingTitle }}
      </button>
      <button
        class="ca-header__mobile-menu-footer-btn rounded-xs ca-header__mobile-menu-footer-btn--primary caption-large flex items-center justify-center py-size-3 px-size-4"
        @click="emit('abrir-conta')"
      >
        {{ abrirContaTitle }}
      </button>
    </div>
  </div>
</template>

<script setup>
import Close from "@assets/fechar.svg";
import ArrowRight from "@assets/arrow-right-green.svg";
import LockIcon from "@assets/lock.svg";
import ChevronDown from "@assets/chevron-down.svg";
import CaStatusTag from "../../base/tags/CaStatusTag.vue";
import { computed } from "vue";
import { onUnmounted, watch } from "vue";

const props = defineProps({
  open: { type: Boolean, default: false },
  isSimplifiedMenu: { type: Boolean, default: false },
  homeItems: { type: Array, default: () => [] },
  simplifiedItems: { type: Array, default: () => [] },
  activeIndex: { type: Number, default: 0 },
  expandedAccordions: { type: Object, default: () => ({}) },
  currentPath: { type: String, default: "" },
  logoLink: { type: String, default: "/" },
  logoImageUrl: { type: String, default: "" },
  homebankingTitle: { type: String, default: "" },
  abrirContaTitle: { type: String, default: "" },
});

const emit = defineEmits([
  "close",
  "select-tab",
  "navigate-category",
  "toggle-accordion",
  "homebanking",
  "abrir-conta",
]);

const activeItem = computed(() => props.homeItems?.[props.activeIndex]);

const isInvestorRelations = computed(() =>
  (activeItem.value?.url || "").includes("investor-relations"),
);

const isAccordionOpen = (sectionIdx) => {
  const key = `${props.activeIndex}-${sectionIdx}`;
  return props.expandedAccordions[key] || false;
};

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  document.body.style.overflow = "";
});
</script>

<style scoped lang="scss">
.ca-header__mobile-menu {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--ca-color-text-inverted);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
  z-index: 1000;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  flex-direction: column;

  &.open {
    opacity: 1;
    pointer-events: all;
    display: flex;
  }
}

.ca-header__mobile-menu-top {
  background-color: var(--ca-color-text-inverted);
  flex-shrink: 0;

  .ca-header__logo-ca {
    display: inline-flex;
    align-items: center;

    img {
      display: block;
    }
  }
}

.ca-header__logo-ca {
  cursor: pointer;
  &:focus-visible {
    outline: 1px solid var(--ca-color-outline-focus-only);
  }
}

.ca-header__mobile-menu-close {
  width: 48px;
  height: 48px;
  background-color: var(--ca-color-neutrals-inverted);
  border: none;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background-color: var(--ca-color-neutrals-on-background-low-10);
  }
  &:focus-visible {
    outline: 1px solid var(--ca-color-outline-focus-only);
  }
}

.ca-header__button-wrapper {
  min-height: 48px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.ca-header__button {
  flex-shrink: 0;
  min-height: 48px;
  background-color: var(--ca-color-text-inverted);
  border: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background-color: var(--ca-color-hover-pressed-container-high-hover);
  }
  &:focus-visible {
    outline: 1px solid var(--ca-color-outline-focus-only);
  }

  &.is-active {
    background-color: var(--ca-color-hover-pressed-container-high-hover);
  }
}

.ca-header__megamenu-separator-mobile {
  border: none;
  border-top: 1px solid var(--ca-color-neutrals-on-background-low-light);
  margin: var(--size-3) var(--size-5);
}

.ca-header__mobile-menu-content {
  flex: 1;
  overflow-y: auto;
}

.ca-header__mobile-megamenu-header-title {
  text-decoration: none;
  cursor: pointer;

  &:focus-visible {
    outline: 1px solid var(--ca-color-outline-focus-only);
  }

  svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }
}

.ca-header__mobile-menu-accordion-title {
  width: 100%;
  background-color: var(--ca-color-neutrals-inverted);
  border: none;
  cursor: pointer;
  color: var(--ca-color-text-headers);
  text-align: left;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
}

.ca-header__mobile-menu-accordion-icon {
  transition: transform 400ms ease-out;
  &.expanded {
    transform: rotate(180deg);
  }

  &--right {
    transform: rotate(-90deg);
  }
}

.ca-header__mobile-menu-accordion-panel {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 400ms ease-out;

  &.expanded {
    grid-template-rows: 1fr;
  }
}

.ca-header__mobile-menu-accordion-list {
  list-style: none;
  padding: 0;
  margin: 0;
  min-height: 0;
  overflow: hidden;
  background-color: var(--ca-color-neutrals-inverted);
}

.ca-header__mobile-menu-accordion-link {
  color: var(--ca-color-text-body-variant);
  text-decoration: none;
  border: none;
  -webkit-tap-highlight-color: transparent;
  background: none;
  cursor: pointer;
  min-height: 48px;

  &:active {
    background-color: var(--ca-color-hover-pressed-container-high-hover);
    border-radius: var(--size-3);
  }
  &:hover {
    background-color: var(--ca-color-hover-pressed-container-high-hover);
    border-radius: var(--size-3);
    padding-left: 0;
  }
  &:focus-visible {
    outline: 1px solid var(--ca-color-outline-focus-only);
  }
}

.ca-header__mobile-menu-footer {
  gap: var(--size-3);
  border-top: 1px solid var(--ca-color-neutrals-on-background-low-light);
  background-color: var(--ca-color-neutrals-inverted);
}

.ca-header__mobile-menu-footer-btn {
  flex: 1;
  gap: var(--size-3);
  min-height: 48px;
  border: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:focus-visible {
    outline: 1px solid var(--ca-color-outline-focus-only);
  }

  &--secondary {
    background-color: var(--ca-color-neutrals-inverted);
    border: 1px solid var(--ca-color-theme-primary);
    color: var(--ca-color-theme-primary);

    &:hover {
      background-color: var(--ca-color-hover-pressed-inverted-hover-light);
    }
  }

  &--primary {
    background-color: var(--ca-color-theme-primary);
    color: var(--ca-color-text-inverted);

    &:hover {
      background-color: var(--ca-color-hover-pressed-primary-pressed);
    }
  }
}

@media (max-width: 1024px) {
  .ca-header__mobile-menu {
    display: block;
  }

  li {
    &:hover {
      padding-left: var(--size-5);
      padding-right: var(--size-5);
    }
    &:active {
      padding-left: var(--size-5);
      padding-right: var(--size-5);
    }
  }

  .ca-header__mobile-menu-top {
    padding-inline: var(--size-7);
    margin-top: var(--size-4);
  }
}
</style>
